import {Demand, DemandList} from "../model/Demand"
import {Divisions} from "../model/Division"
import {Regions} from "./../model/Region"
import {UniversityBranches} from "./../model/UniversityBranch"
import {Specialities} from "./../model/Speciality"

import {SheetData} from "./../Excel/export"
import {getCompany, lpad, RowData, getRegion} from "./../model/utils/utils"
import {User} from "../model/User";
import {Employee} from "../model/Employee";
import {Vacancy} from "../model/Vacancy";
import {
    border,
    Columns,
    EMPLOYEE_YEAR,
    makeEmptyRow,
    makeSheet,
    makeSpecialityData,
    makeUniversityData,
    Rows,
    specialityColumns,
    specialityMerges,
    universityColumns,
    universityMerges,
    VACANCY_YEARS
} from "./common";

const columnsOrder = [
    ...specialityColumns,
    Columns.isOpk,
    Columns.year,
    Columns.type,
    Columns.demand,
    Columns.position,
    Columns.gpa,
    Columns.age,
    Columns.target,
    Columns.targetOpk,
    Columns.gender,
    Columns.fired,
    Columns.businessType,
    Columns.employmentRegion,
    Columns.country,
    Columns.notes,
    Columns.employedBeforeGraduation,
    Columns.constructionTeam,
    Columns.hiPo,
    Columns.hiPoType,
    Columns.trained,
    Columns.practiceEducational,
    Columns.practiceUndergraduate,
    Columns.details,
    Columns.detailsGraduate,
    ...universityColumns,
]

type Data = {
    demand: DemandList
    divisions: Divisions
    regions: Regions
    specialities: Specialities
    universityBranches: UniversityBranches
    meta: {
        company: string
        user: User
    }
}

const composeData = ({
    demand,
    divisions,
    regions,
    specialities,
    universityBranches,
}: Data): RowData[] => {
    let rawResult: RowData[] = []

    demand.map(({
        id,
        companyId,
        courseOfStudyName,
        specialityId,
        regionId,
        universityBranchId,
        vacancies,
        employees,
        nationalProjectName,
        englishLevelName,
        languageName,
        businessTypeName,
    }: Demand) => {
        const {
            isOpk,
        } = getCompany(divisions, companyId)

        vacancies.filter(({year}: Vacancy) => year >= Math.min(...VACANCY_YEARS)).map(({
            id: vacancyId,
            year,
            title,
            demand,
            hiPo,
            hiPoTypeName,
            countryName,
            notes,
        }: Vacancy) => {
            let row = makeEmptyRow(columnsOrder, {
                [Columns.year]: year,
                [Columns.type]: "план",
                [Columns.position]: title,
                [Columns.demand]: demand,
                [Columns.businessType]: businessTypeName,
                [Columns.isOpk]: (isOpk/* && specialitiesOpk.has(specialityId)*/) ? "Да" : "Нет",
                [Columns.employmentRegion]: "",
                [Columns.country]: countryName,
                [Columns.notes]: notes,
                [Columns.hiPo]: hiPo || "",
                [Columns.hiPoType]: hiPo ? hiPoTypeName : "",
                ...makeSpecialityData({
                    id: lpad(id) + "." + lpad(vacancyId),
                    courseOfStudyName,
                    divisions,
                    companyId,
                    specialities,
                    specialityId,
                    nationalProjectName,
                    englishLevelName,
                    languageName,
                }),
                ...makeUniversityData({
                    regions,
                    regionId,
                    universityBranches,
                    universityBranchId,
                }),
            })

            rawResult = rawResult.concat([row])
        })

/*
        const {
            specialitiesOpk,
        } = getUniversityBranch(universityBranches, universityBranchId)
*/

        const targetOpk = getCompany(divisions, companyId).isOpk/* && specialitiesOpk.has(specialityId)*/

        employees.filter(({demandYear}: Employee) => demandYear >= EMPLOYEE_YEAR).map(({
            id: employeeId,
            target,
            gpa,
            position,
            age,
            genderName,
            firedName,
            employedBeforeGraduation,
            constructionTeam,
            hiPo,
            hiPoTypeName,
            trained,
            practiceEducational,
            practiceUndergraduate,
            details,
            detailsGraduate,
            demandYear,
            employmentRegionId,
        }: Employee) => {
            const {
                name: employmentRegionName,
            } = getRegion(regions, employmentRegionId)
    
            let row = makeEmptyRow(columnsOrder, {
                [Columns.year]: demandYear,
                [Columns.type]: "факт",
                [Columns.demand]: 1,
                [Columns.position]: position,
                [Columns.gpa]: gpa,
                [Columns.age]: age,
                [Columns.target]: target ? "Целевик" : "",
                [Columns.targetOpk]: target && targetOpk ? "Целевое обучение ОПК" : "",
                [Columns.gender]: genderName,
                [Columns.fired]: firedName,
                [Columns.businessType]: businessTypeName,
                [Columns.isOpk]: (isOpk/* && specialitiesOpk.has(specialityId)*/) ? "Да" : "Нет",
                [Columns.employmentRegion]: employmentRegionName,
                [Columns.country]: "",
                [Columns.notes]: "",
                [Columns.employedBeforeGraduation]: employedBeforeGraduation ? "да" : "",
                [Columns.constructionTeam]: constructionTeam ? "да" : "",
                [Columns.hiPo]: hiPo ? "да" : "",
                [Columns.hiPoType]: hiPo ? hiPoTypeName : "",
                [Columns.trained]: trained ? "Прошел производственную практику" : "",
                [Columns.practiceEducational]: practiceEducational ? "Прошел учебную практику" : "",
                [Columns.practiceUndergraduate]: practiceUndergraduate ? "Прошел преддипломную практику" : "",
                [Columns.details]: details,
                [Columns.detailsGraduate]: detailsGraduate,
                ...makeSpecialityData({
                    id: lpad(id) + "." + lpad(employeeId),
                    courseOfStudyName,
                    divisions,
                    companyId,
                    specialities,
                    specialityId,
                    nationalProjectName,
                    englishLevelName,
                    languageName,
                }),
                ...makeUniversityData({
                    regions,
                    regionId,
                    universityBranches,
                    universityBranchId,
                }),

            })

            rawResult = rawResult.concat([row])
        })
    })

    return rawResult
}


export const vacanciesWs = (data: Data): SheetData => makeSheet({
    sheetTitle: "факт и план Трудоустройства",
    title1: "Данные о плане приема и трудоустройстве выпускников",
    title2: 'в организации Госкорпорации "Росатом".',
    company: data.meta.company,
    fullName: data.meta.user.executor.fullName,
    columnsOrder,
    heading1Row: {
        [Columns.year]:                   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Год"},
        [Columns.type]:                   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Статус закрытия вакансии"},
        [Columns.demand]:                 {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Количество"},
        [Columns.position]:               {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Анкета"},
    },
    heading2Row: {
        [Columns.position]:                 {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Должность"},
        [Columns.gpa]:                      {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Ср. балл"},
        [Columns.age]:                      {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Возраст"},
        [Columns.target]:                   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Целевик"},
        [Columns.targetOpk]:                {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Целевое обучение ОПК"},
        [Columns.gender]:                   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Пол"},
        [Columns.fired]:                    {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Уволенный"},
        [Columns.businessType]:             {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Направление бизнеса"},
        [Columns.employmentRegion]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион трудоустройства"},
        [Columns.country]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Гражданство"},
        [Columns.notes]:                    {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Комментарий"},
        [Columns.employedBeforeGraduation]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Был ли выпускник трудоустроен до получения диплома"},
        [Columns.constructionTeam]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Участник стройотрядов атомной отрасли"},
        [Columns.hiPo]:                     {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "HiPo"},
        [Columns.hiPoType]:                 {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Основание для включения в HiPo"},
        [Columns.trained]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Прошел производственную практику"},
        [Columns.practiceEducational]:      {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Прошел учебную практику"},
        [Columns.practiceUndergraduate]:    {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Прошел преддипломную практику"},
        [Columns.details]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Прочее"},
        [Columns.detailsGraduate]:          {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Прочие сведения о выпускнике"},
    },
    merges: [
        ...specialityMerges,

        [Columns.year,                  Rows.Heading1, Columns.year,                 Rows.Heading3],
        [Columns.type,                  Rows.Heading1, Columns.type,                 Rows.Heading3],
        [Columns.demand,                Rows.Heading1, Columns.demand,               Rows.Heading3],
        [Columns.isOpk,                 Rows.Heading1, Columns.isOpk,                Rows.Heading3],

        [Columns.position,        Rows.Heading1, Columns.detailsGraduate,      Rows.Heading1],

        [Columns.position,                 Rows.Heading2, Columns.position,                 Rows.Heading3],
        [Columns.gpa,                      Rows.Heading2, Columns.gpa,                      Rows.Heading3],
        [Columns.age,                      Rows.Heading2, Columns.age,                      Rows.Heading3],
        [Columns.target,                   Rows.Heading2, Columns.target,                   Rows.Heading3],
        [Columns.targetOpk,                Rows.Heading2, Columns.targetOpk,                Rows.Heading3],
        [Columns.gender,                   Rows.Heading2, Columns.gender,                   Rows.Heading3],
        [Columns.fired,                    Rows.Heading2, Columns.fired,                    Rows.Heading3],
        [Columns.businessType,             Rows.Heading2, Columns.businessType,             Rows.Heading3],
        [Columns.employmentRegion,         Rows.Heading2, Columns.employmentRegion,         Rows.Heading3],
        [Columns.country,                  Rows.Heading2, Columns.country,                  Rows.Heading3],
        [Columns.notes,                    Rows.Heading2, Columns.notes,                    Rows.Heading3],
        [Columns.employedBeforeGraduation, Rows.Heading2, Columns.employedBeforeGraduation, Rows.Heading3],
        [Columns.constructionTeam,         Rows.Heading2, Columns.constructionTeam,         Rows.Heading3],
        [Columns.hiPo,                     Rows.Heading2, Columns.hiPo,                     Rows.Heading3],
        [Columns.hiPoType,                 Rows.Heading2, Columns.hiPoType,                 Rows.Heading3],
        [Columns.trained,                  Rows.Heading2, Columns.trained,                  Rows.Heading3],
        [Columns.practiceEducational,      Rows.Heading2, Columns.practiceEducational,      Rows.Heading3],
        [Columns.practiceUndergraduate,    Rows.Heading2, Columns.practiceUndergraduate,    Rows.Heading3],
        [Columns.details,                  Rows.Heading2, Columns.details,                  Rows.Heading3],
        [Columns.detailsGraduate,          Rows.Heading2, Columns.detailsGraduate,          Rows.Heading3],

        ...universityMerges,
    ],
    data: composeData(data),
    specialities: data.specialities,
})
